import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentsApi, type UpdatePaymentSettingsPayload } from '@/api/payments';

export const paymentKeys = {
  settings: (competitionId: string) => ['payments', competitionId, 'settings'] as const,
  mine: (competitionId: string) => ['payments', competitionId, 'mine'] as const,
};

export function usePaymentSettings(competitionId: string | undefined) {
  return useQuery({
    queryKey: paymentKeys.settings(competitionId ?? ''),
    queryFn: () => paymentsApi.getSettings(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useUpdatePaymentSettings(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePaymentSettingsPayload) => paymentsApi.updateSettings(competitionId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKeys.settings(competitionId), data);
      queryClient.invalidateQueries({ queryKey: paymentKeys.mine(competitionId) });
    },
  });
}

/** What the signed-in competitor owes and the ways to pay it. Polls briefly after a card payment so "paid" appears by itself. */
export function useMyPayment(competitionId: string | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: paymentKeys.mine(competitionId ?? ''),
    queryFn: () => paymentsApi.mine(competitionId as string),
    enabled: Boolean(competitionId),
    retry: false,
    refetchInterval: (query) => (options?.poll && query.state.data && !query.state.data.hasPaid ? 3000 : false),
  });
}

export function useConfirmCheckout(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => paymentsApi.confirm(competitionId, sessionId),
    onSuccess: (data) => queryClient.setQueryData(paymentKeys.mine(competitionId), data),
  });
}

export function useStartCheckout(competitionId: string) {
  return useMutation({ mutationFn: () => paymentsApi.checkout(competitionId) });
}
